/**
 * Navigation Constants
 *
 * Centralized constants for all navigation paths and URL patterns.
 * Makes it easy to update routes across the entire application.
 */

// ====================================================
// URL Parameters
// ====================================================

/**
 * URL Parameters
 */
export const PARAMS = {
  CERTIFICATION_ID: 'certificationId',
  SIMULATION_ID: 'simulationId',
  SESSION_ID: 'sessionId',
  PERSONA_ID: 'personaId',
  SCORE_ID: 'scoreId',
  USER_ID: 'userId',
} as const;

// ====================================================
// Application Routes
// ====================================================

/**
 * All application routes (includes all possible routes regardless of feature flags)
 */
export const ROUTES = {
  // Landing/Public pages
  HOME: '/',

  // Authentication routes
  LOGIN: '/auth/login',
  SSO_CALLBACK: '/auth/callback',
  LOGOUT: '/logout',

  // Sales Rep protected routes
  SALES_REP_DASHBOARD: '/sales-rep',
  CERTIFICATION_OVERVIEW: `/sales-rep/certifications/{${PARAMS.CERTIFICATION_ID}}`,
  SIMULATION_PREP: `/sales-rep/certifications/{${PARAMS.CERTIFICATION_ID}}/simulations/{${PARAMS.SIMULATION_ID}}/prep`,
  SIMULATION_ROLEPLAY: `/sales-rep/certifications/{${PARAMS.CERTIFICATION_ID}}/simulations/{${PARAMS.SIMULATION_ID}}/roleplay`,
  SIMULATION_SCORE: `/sales-rep/certifications/{${PARAMS.CERTIFICATION_ID}}/simulations/{${PARAMS.SIMULATION_ID}}/score`,
  SIMULATION_CERT: `/sales-rep/certifications/{${PARAMS.CERTIFICATION_ID}}/certificate`,

  // Admin/Manager protected routes
  MANAGER_DASHBOARD: '/manager',
  MANAGER_CERTIFICATIONS: '/manager/certifications',
  MANAGER_USERS: '/manager/users',
  MANAGER_ANALYTICS: '/manager/analytics',

  // Other routes can be added here as needed
  USER_PROFILE: '/profile',
} as const;

/**
 * Routes available to all users (no authentication required)
 */
export const SHARED_ROUTES = [ROUTES.LOGIN, ROUTES.LOGOUT, ROUTES.SSO_CALLBACK] as const;

/**
 * Routes available to Sales Rep users
 * Filtered based on feature flags
 */
export const SALES_REP_ROUTES = [
  ...SHARED_ROUTES,
  ROUTES.CERTIFICATION_OVERVIEW,
  ROUTES.SALES_REP_DASHBOARD,
  ROUTES.SIMULATION_PREP,
  ROUTES.SIMULATION_ROLEPLAY,
  ROUTES.SIMULATION_SCORE,
  ROUTES.USER_PROFILE,
] as const;

/**
 * Routes available to Admin/Manager users
 * Filtered based on feature flags
 */
export const MANAGER_ROUTES = [
  ...SHARED_ROUTES,
  ROUTES.MANAGER_ANALYTICS,
  ROUTES.MANAGER_CERTIFICATIONS,
  ROUTES.MANAGER_DASHBOARD,
  ROUTES.MANAGER_USERS,

  // Admins have access to Sales Rep routes as well
  ROUTES.CERTIFICATION_OVERVIEW,
  ROUTES.SALES_REP_DASHBOARD,
  ROUTES.SIMULATION_PREP,
  ROUTES.SIMULATION_ROLEPLAY,
  ROUTES.SIMULATION_SCORE,
  ROUTES.USER_PROFILE,
] as const;

/**
 * Type for route keys
 */
export type RouteKey = keyof typeof ROUTES;
